import Papa from 'papaparse';
import { PDFDocument, StandardFonts } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import { constants } from './constants';
import { getCRMURL } from 'src/js/api';

const locale = 'de-DE';
const crmUrl = process.env.CRM_URL;

export function formatPrice(price) {
  let parsedPrice = parseFloat(price);

  if (typeof parsedPrice !== 'number' || isNaN(parsedPrice)) {
    parsedPrice = 0;
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  }).format(parsedPrice);
}

export function formatDate(timestamp) {
  if (!timestamp) return;

  const date = new Date(timestamp);
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
}

export function parseDate(date) {
  if (!date) return;

  const [d, m, y] = date.split('.');
  return new Date(Date.UTC(y, m - 1, d)).toISOString();
}

export function mapOrderStatus(status) {
  const val = constants.orderStatusOptions.find((s) => s.value === status);

  return val?.label;
}

export function isSameOrder(orderA, orderB) {
  const isSameDeliveryType = orderA.deliveryType === orderB.deliveryType;
  const isSameCustomer = orderA.customer.email === orderB.customer.email;
  const isSameDeliveryPrice = parseFloat(orderA.deliveryPrice || 0) === parseFloat(orderB.deliveryPrice || 0);

  if (orderA.items.length !== orderB.items.length) return false;

  for (let i = 0; i < orderA.items.length; i++) {
    let item1 = orderA.items[i];
    let item2 = orderB.items[i];

    const parsedAmount1 = parseFloat(item1.amount || 0);
    const parsedAmount2 = parseFloat(item2.amount || 0);
    const parsedPrice1 = parseFloat(item1.price || 0);
    const parsedPrice2 = parseFloat(item2.price || 0);

    if (item1.id !== item2.id || parsedAmount1 !== parsedAmount2 || parsedPrice1 !== parsedPrice2) {
      return false;
    }
  }

  return isSameDeliveryType && isSameCustomer && isSameDeliveryPrice;
}

export function formatNumberInput(value) {
  value = String(value).replace('.', ',');

  let sanitizedValue = value.replace(/[^0-9,]/g, '');

  const parts = sanitizedValue.split(',');
  let integerPart = parts[0];
  let decimalPart = parts.length > 1 ? parts.slice(1).join('') : '';

  if (decimalPart.length > 2) {
    const numericValue = parseFloat(`${integerPart || '0'}.${decimalPart}`);

    if (!isNaN(numericValue)) {
      const [fixedInteger, fixedDecimal = ''] = numericValue.toFixed(2).split('.');
      integerPart = fixedInteger;
      decimalPart = fixedDecimal;
    } else {
      decimalPart = decimalPart.slice(0, 2);
    }
  }

  // Remove trailing zeros from decimal part
  if (decimalPart) {
    decimalPart = decimalPart.replace(/0+$/, '');
  }

  sanitizedValue = integerPart + (decimalPart ? ',' + decimalPart : '');

  if (sanitizedValue.startsWith(',')) {
    sanitizedValue = '0' + sanitizedValue;
  }

  if (sanitizedValue.endsWith(',')) {
    sanitizedValue = sanitizedValue.slice(0, -1);
  }

  return sanitizedValue;
}

export function safeParseFloat(value) {
  if (typeof value === 'number') {
    return parseFloat(value);
  }

  if (typeof value !== 'string') {
    return 0;
  }
  value = value.trim();
  value = String(value).replace(',', '.');

  const parsedValue = parseFloat(value);

  if (isNaN(parsedValue)) {
    return 0;
  }

  return parsedValue;
}

export function safeParseString(value = '') {
  if (typeof value !== "number" || isNaN(value)) {
    return value;
  }
  return String(value)?.trim()?.replace('.', ',');
}

export async function getOrderSource(api, excludeNA = true) {
  const data = await api.fetchOrderSource();
  const options = data?.[0]?.options?.choices?.map((val) => ({label: val?.value === 'ansay' ? 'TePo Ansay': val?.text, value: val?.value}))
  if (!excludeNA) {
    options.push({ label: 'n.a.', value: null })
  }
  return options || []
}

export async function getCustomerGroup(api, excludeNA = false) {
  const data = await api.fetchCustomerGroup();
  // Drop "no group" choices stored in the DB (e.g. n.a. with an empty value); n.a. is added below
  const options = (data?.[0]?.options?.choices || [])
    .filter((val) => hasCustomerGroup(val?.value) && val?.text?.trim().toLowerCase() !== 'n.a.')
    .map((val) => ({label: val.text, value: val.value}))
  if (!excludeNA) {
    options.push({ label: 'n.a.', value: null })
  }
  return options
}

// '', 'n.a.' and the legacy 'standard' value all mean "no customer group assigned"
export function hasCustomerGroup(group) {
  return group != null && !['', 'n.a.', 'standard'].includes(group)
}

export function safeParseJSON(string) {
  if(!string) return {}
  try {
    return JSON.parse(string);
  } catch (error) {
    console.log(error);
    return {};
  }
}

export function parseAddress(address) {

  const defaultAddress =  {
    formatted_address: 'keine Adresse',
    components: {
      street_number: '',
      route: '',
      postal_code: '',
      country: 'Deutschland',
      locality: '',
      message: ''
    },
    name: '',
    surname: '',
  }

  if(!address) return defaultAddress

  if(typeof address === 'object'){
    const parsedAddress = Object.assign(defaultAddress, address);
    
    // Preserve packstation flags if they exist in the original address
    if (address.components?.isPackstation !== undefined) {
      parsedAddress.components.isPackstation = address.components.isPackstation;
    }
    if (address.components?.packstationType) {
      parsedAddress.components.packstationType = address.components.packstationType;
    }
    
    return parsedAddress;
  }

  const parsedAddress = Object.assign(defaultAddress, safeParseJSON(address))
  
  // Preserve packstation flags if they exist in the parsed JSON
  if (safeParseJSON(address)?.components?.isPackstation !== undefined) {
    parsedAddress.components.isPackstation = safeParseJSON(address).components.isPackstation;
  }
  if (safeParseJSON(address)?.components?.packstationType) {
    parsedAddress.components.packstationType = safeParseJSON(address).components.packstationType;
  }
  
  return parsedAddress
}

export function stringifyAddress(address) {
  try {
    if (!address) {
      return null;
    }

    const addressParts = [
      address?.components?.route?.replaceAll(",", " "),
      address?.components?.street_number?.replaceAll(",", " "),
      address?.components?.postal_code?.replaceAll(",", " "),
      address?.components?.locality?.replaceAll(",", " "),
      "Deutschland",
      address?.components?.message?.replaceAll(",", " "),
    ].filter(Boolean);

    const addressJSON = {
      formatted_address: addressParts.join(", "),
      components: {
        street_number: address.components?.street_number || "",
        route: address.components?.route || "",
        message: address.components?.message || "",
        postal_code: address.components?.postal_code || "",
        locality: address.components?.locality || "",
        country: address.components?.country || "",
      },
      name: address.name || "",
      surname: address.surname || "",
    };

    // Preserve packstation flags if they exist
    if (address.components?.isPackstation !== undefined) {
      addressJSON.components.isPackstation = address.components.isPackstation;
    }
    if (address.components?.packstationType) {
      addressJSON.components.packstationType = address.components.packstationType;
    }

    return JSON.stringify(addressJSON);
  } catch (error) {
    console.warn('Error stringifying address:', error, address);
    return null;
  }
}


export function isEqualAddresses(addressA, addressB) {

  if (!addressA || !addressB) return false;

  if(addressA.components.street_number !== addressB.components.street_number ||
    addressA.components.route !== addressB.components.route ||
    addressA.components.postal_code !== addressB.components.postal_code ||
    addressA.components.country !== addressB.components.country ||
    addressA.components.locality !== addressB.components.locality ||
    addressA.components.message !== addressB.components.message ||
    addressA.components.message !== addressB.components.message ||
    addressA.name !== addressB.name ||
    addressA.surname !== addressB.surname ||
    addressA.components.isPackstation !== addressB.components.isPackstation ||
    addressA.components.packstationType !== addressB.components.packstationType
  ){
    return false
  }

  return true;
}

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function replaceWithValue(body, variableList) {
  const valueMap = new Map();

  variableList?.forEach(({ label,value }) => {
    valueMap.set(label, value);
  });

  const labels = [...valueMap.keys()]?.map(escapeRegex)
  const pattern = new RegExp(`@(${labels.join('|')})`, 'g');

  return body?.replace(pattern, (_, label) => valueMap?.get(label) || _);
}

function replaceWithLabel(body, variableList) {
  const labelMap = new Map();

  variableList?.forEach(({ label,value }) => {
    labelMap.set(value, label);
  });

  const variables = [...labelMap.keys()].map(escapeRegex)
  const pattern = new RegExp(`(${variables?.join('|')})`, 'g');

  return body?.replace(pattern, (_, label) => `@${labelMap?.get(label)}` || _);
}

export function replacePlaceholders(body, mode , variableList = []) {
  if (mode === 'encode') 
    return replaceWithValue(body, variableList);

  else if (mode === 'decode') 
    return replaceWithLabel(body, variableList);
     
}

export function getImageUrl(id , isGreeners = false) {
  if (!id) return null;

  const baseUrl = isGreeners ? process.env.GREENERS_URL : process.env.CRM_URL;
  const imageUrl = `${baseUrl}assets/${id}`;
  return imageUrl;
}


export function getFailedValidationMessage(validation) {
    switch (validation) {
        case 'max-file-size':
          return `Die Datei überschreitet die maximale Dateigröße`

        case 'duplicate':
          return `Datei existiert bereits`

        default:
          return `Dateivalidierung fehlgeschlagen: (${ validation })`
       }
}

export async function updateUserColumns(api, userId, columnsData, fieldName ) {
  try {
      const response = await api.items('directus_users').updateOne(
          userId,
          { [fieldName]: columnsData },
          { fields: [fieldName] }
      );
      return response[fieldName];
  } catch (error) {
      console.error('Error updating setting:', error);
      throw error;
  }
}

import {
  getExistingProviderSurchargeId,
  createProviderSurcharge,
  updateProviderSurcharge
} from 'src/js/api';

/**
 * Save surcharge for a single provider (create, update, or delete).
 * Uses api.js Greeners REST helpers.
 * @param {string} pharmacyId - UUID of pharmacy
 * @param {string} providerId - UUID of prescription_provider
 * @param {number|null} value - surcharge value, null to remove
 * @param {string} unit - '%' or '€'
 * @param {string} [token] - Greeners auth token (defaults to localStorage)
 */
export async function saveProviderSurcharge(pharmacyId, providerId, value, unit, token = localStorage.getItem('greeners_auth_token')) {
  const existingId = await getExistingProviderSurchargeId(pharmacyId, providerId, token);

  if (value === null || value === '' || (typeof value === 'number' && isNaN(value))) {
    if (existingId) {
      // Some roles cannot delete this collection; clear by update instead.
      await updateProviderSurcharge(existingId, {
        pharmacy_id: pharmacyId,
        provider_id: providerId,
        surcharge_value: null,
        surcharge_unit: unit || '%'
      }, token);
    }
    return;
  }

  const payload = {
    pharmacy_id: pharmacyId,
    provider_id: providerId,
    surcharge_value: value,
    surcharge_unit: unit || '%'
  };

  if (existingId) {
    await updateProviderSurcharge(existingId, payload, token);
  } else {
    await createProviderSurcharge(payload, token);
  }
}

// ── Cansativa order terms (AGB 2.2 and 4.2) ─────────────────────────────────
// All three are net values, i.e. before VAT.
export const CANSATIVA_MIN_ORDER_NET = 40;
export const CANSATIVA_FREE_SHIPPING_NET = 100;
export const CANSATIVA_SHIPPING_FEE = 20;

// Flat fee per order below the threshold, regardless of quantity, size or weight.
export function cansativaShippingFee(netTotal) {
  const net = Number(netTotal) || 0;
  if (net <= 0) return 0;

  return net >= CANSATIVA_FREE_SHIPPING_NET ? 0 : CANSATIVA_SHIPPING_FEE;
}

// ── Who may order from Cansativa ────────────────────────────────────────────
export const CANSATIVA_FLAG = 'cansativa_orders_enabled';

// Columns only buyers get to see. The middle two are commented out in
// constants.js right now, but stay listed so they are hidden when they return.
export const CANSATIVA_COLUMNS = [
  'Cansativa',
  'Mindestverfugbarkeit',
  'Einkaufsvorschlag',
  'Bestellung'
];

// `user` can be the store object ({ cu, greeners }) or just the `cu` part.
// The flag is set per user, so read own_allowed_operations — allowed_operations
// holds the shared pharmacy's flags instead.
export function canUseCansativa(user) {
  const cu = user?.cu ?? user;
  const userFlags = Array.isArray(cu?.own_allowed_operations) ? cu.own_allowed_operations : [];
  const hasBtmNumber = !!String(cu?.pharmacy_btm_number ?? '').trim();

  const hasApiKey = !!String(cu?.pharmacy_cansativa_api_key ?? '').trim();
  return userFlags.includes(CANSATIVA_FLAG) && hasBtmNumber && hasApiKey;
}

// Always two decimals with a comma, e.g. 12 -> 12,00 € and 12.345 -> 12,35 €.
export function formatCansativaPrice(value) {
  const amount = Number.isFinite(Number(value)) ? Number(value) : 0;

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

export function removeCansativaColumns(columns, user) {
  if (canUseCansativa(user)) return columns;

  return Object.fromEntries(
    Object.entries(columns).filter(([columnName]) => !CANSATIVA_COLUMNS.includes(columnName))
  );
}

export async function getUserColumns(api, userId, fieldName, initialColumns) {
  let directusColumns = {};
  try {
    const userPayload = await api.items('directus_users').readOne(userId, {
      fields: [fieldName]
    });
    directusColumns = userPayload?.[fieldName] ?? {};
  } catch (error) {
    console.error('Error fetching columns:', error);
    throw error;
  }

  if (fieldName === 'table_columns_prescriptions') {
    return directusColumns;
  }

      const mergedColumns = {};
      for (const key in initialColumns) {
        if (directusColumns?.hasOwnProperty(key)) {
          mergedColumns[key] = directusColumns[key];
        } else {
          mergedColumns[key] = initialColumns[key];
        }
      }
      
      const nameMappings = {
        'Preis pro G': 'Preis pro g',
        'Preis pro Ml': 'Preis pro ml',
        'Netto EK pro G': 'Netto EK pro g',
        'Netto EK pro Ml': 'Netto EK pro ml',
        'Reservierung': 'Ware im Zulauf / Rückstellung',
        'Reservierung beim Lieferanten': 'Ware im Zulauf / Rückstellung',
        'Reserviert beim Lieferant': 'Ware im Zulauf / Rückstellung'
      };
      
      for (const key in mergedColumns) {
        if (mergedColumns[key]?.name && nameMappings[mergedColumns[key].name]) {
          mergedColumns[key].name = nameMappings[mergedColumns[key].name];
        }
      }
      
      return mergedColumns;
}

export function getProductDetails(inventory) {
  if (!Array.isArray(inventory) || inventory.length === 0) {
    return { netpurchaseprice_per_unit: "n.a.", expiryDate: "n.a." };
  }

  const available = inventory.filter(item => item?.availability === true);
  const unavailable = inventory.filter(item => item?.availability === false);

  let chosen = null;

  if (inventory?.length === 1) {
    chosen = inventory[0];
  } else if (available?.length === 1) {
    chosen = available[0];
  } else if (available?.length > 1) {
    chosen = getLowestByPrice(available);
  } else if (unavailable?.length === 1) {
    chosen = unavailable[0];
  }  else if (unavailable?.length > 1) {
    chosen = getLowestByPrice(unavailable);
  }
  return chosen
}

function getLowestByPrice(items) {
  return items?.reduce((lowest, current) =>
    current?.brutto_retail_price < lowest?.brutto_retail_price ? current : lowest
  );
}
/**
 * Preserves DHL options when clearing localStorage
 * @returns {Object} Object containing preserved DHL options
 */
export function preserveDhlAndPopupOptions() {
  const dhlOptionsKeys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      if (key.startsWith('dhl_options_') || key.includes('disable_invoice_popup') || key.startsWith('bundle_notice_dismissed_')) {
        dhlOptionsKeys.push(key);
      }
    }
  }
  
  // Store DHL options temporarily
  const preservedDhlOptions = {};
  dhlOptionsKeys.forEach(key => {
    preservedDhlOptions[key] = localStorage.getItem(key);
  });
  
  return preservedDhlOptions;
}

/**
 * Restores DHL options to localStorage
 * @param {Object} preservedDhlOptions - Object containing preserved DHL options
 */
export function restoreDhlOptions(preservedDhlOptions) {
  Object.keys(preservedDhlOptions).forEach(key => {
    localStorage.setItem(key, preservedDhlOptions[key]);
  });
}

/**
 * Clears localStorage while preserving DHL options
 */
export function clearLocalStoragePreservingDhl() {
  const preservedDhlOptions = preserveDhlAndPopupOptions();
  localStorage.clear();
  restoreDhlOptions(preservedDhlOptions);
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function getProductMovementsHeaderRows(summaryData = {}) {
  return [
    { label: 'Exportdatum', value: summaryData.exportdatum || '' },
    { label: 'Software Version', value: summaryData.softwareVersion || '' },
    { label: 'Name', value: summaryData.name || '' },
    { label: 'Adress', value: summaryData.adresse || '' },
    { label: 'Tax ID', value: summaryData.taxId || '' },
    { label: 'Auswertungszeitraum (von/bis)', value: summaryData.auswertungszeitraum || '' },
  ];
}

export function downloadXLSX(data, filename, sheetName = 'Sheet1', summaryData = null, options = {}) {
  const { utils, writeFile } = require('xlsx');
  const workbook = utils.book_new();
  
  // Create worksheet from data
  const worksheet = utils.json_to_sheet(data);
  
  // If summary data is provided, place it in specific cells at the top
  if (summaryData && Array.isArray(summaryData)) {
    // Extract summary values from the array
    let apothekenname = '';
    let adresse = '';
    let erstellungsdatum = '';
    let stichtag = '';
    let lagerwert = '';
    let auswertungszeitraum = '';
    let exportdatum = '';
    let softwareVersion = '';
    let name = '';
    let taxId = '';
    const isProductMovementsHeader = options.headerFormat === 'productMovements';
    const productMovementsSummary = options.summaryObject || null;
    
    summaryData.forEach(row => {
      if (row && typeof row === 'object') {
        if (row['Apothekenname']) apothekenname = row['Apothekenname'];
        if (row['Adresse']) adresse = row['Adresse'];
        if (row['Erstellungsdatum des Berichts']) erstellungsdatum = row['Erstellungsdatum des Berichts'];
        if (row['Stichtag der Auswertung']) stichtag = row['Stichtag der Auswertung'];
        if (row['Exportdatum']) exportdatum = row['Exportdatum'];
        if (row['Software Version']) softwareVersion = row['Software Version'];
        if (row['Name']) name = row['Name'];
        if (row['Adress']) adresse = row['Adress'] || adresse;
        if (row['Tax-ID']) taxId = row['Tax-ID'];
        if (row['Lagerwert']) {
          // Extract the value from "Lagerwert: {amount}" format or use as-is
          const lagerwertStr = row['Lagerwert'];
          if (lagerwertStr.includes(':')) {
            lagerwert = lagerwertStr.split(':')[1].trim();
          } else {
            lagerwert = lagerwertStr;
          }
        }
        if (row['Auswertungszeitraum (von/bis)']) auswertungszeitraum = row['Auswertungszeitraum (von/bis)'];
      }
    });
    
    // Get the range of the existing data
    const dataRange = utils.decode_range(worksheet['!ref'] || 'A1');

    // First, collect all non-empty summary fields dynamically
    const summaryFields = isProductMovementsHeader
      ? getProductMovementsHeaderRows(productMovementsSummary || {
          exportdatum,
          softwareVersion,
          name,
          adresse,
          taxId,
          auswertungszeitraum,
        })
      : [
      { label: 'Apothekenname:', value: apothekenname },
      { label: 'Adresse:', value: adresse },
      { label: 'Erstellungsdatum des Berichts:', value: erstellungsdatum },
      { label: 'Stichtag der Auswertung:', value: stichtag },
      // Add Lagerwert only for "Im Bericht verwendete Daten" report (when lagerwert exists)
      ...(lagerwert ? [{ label: 'Lagerwert:', value: lagerwert }] : []),
      // Exclude "Auswertungszeitraum (von/bis)" from Lagerverfügbarkeitsbericht report
      ...(filename.includes('Lagerverfügbarkeit') ? [] : [{ label: 'Auswertungszeitraum (von/bis):', value: auswertungszeitraum }])
    ].filter(field => field.value); // Filter out empty values

    // Shift data down by summary rows + 1 (for empty row between summary and data)
    const shiftRows = summaryFields.length + 1;
    
    // Create a new range for shifted data
    const newRange = {
      s: { r: dataRange.s.r + shiftRows, c: dataRange.s.c },
      e: { r: dataRange.e.r + shiftRows, c: dataRange.e.c }
    };
    
    // Create new worksheet object
    const newWorksheet = {};

    // Place summary labels and values in consecutive rows starting from row 1
    summaryFields.forEach((field, index) => {
      const row = index + 1; // Start from row 1 (0-indexed as 0)
      newWorksheet[`A${row}`] = { t: 's', v: field.label };
      if (!field.isSection) {
        newWorksheet[`B${row}`] = { t: 's', v: `${String(field.value ?? '')}` };
      }
    });
    
    // Now shift all existing cells down
    Object.keys(worksheet).forEach(key => {
      if (key.startsWith('!')) {
        // Keep metadata keys, but update ref later
        if (key !== '!ref') {
          newWorksheet[key] = worksheet[key];
        }
      } else {
        // Shift cell references
        const cellAddress = utils.decode_cell(key);
        const newAddress = utils.encode_cell({
          r: cellAddress.r + shiftRows,
          c: cellAddress.c
        });
        newWorksheet[newAddress] = worksheet[key];
      }
    });
    
    // Update the range to include summary area
    const finalRange = {
      s: { r: 0, c: 0 },
      e: { r: newRange.e.r, c: Math.max(newRange.e.c, 3) } // At least column D for summary
    };
    newWorksheet['!ref'] = utils.encode_range(finalRange);
    
    // Calculate column widths based on content
    const maxCol = finalRange.e.c;
    const colWidths = [];
    
    for (let col = 0; col <= maxCol; col++) {
      let maxWidth = 10; // Minimum width
      
      // Check all cells in this column
      for (let row = 0; row <= finalRange.e.r; row++) {
        const cellAddress = utils.encode_cell({ r: row, c: col });
        const cell = newWorksheet[cellAddress];
        
        if (cell && cell.v) {
          // Calculate width based on content length
          const cellValue = String(cell.v);
          // Approximate width: 1 character ≈ 1 unit, but add padding
          const estimatedWidth = cellValue.length * 1.2;
          if (estimatedWidth > maxWidth) {
            maxWidth = Math.min(estimatedWidth, 50); // Cap at 50 to prevent extremely wide columns
          }
        }
      }
      
      colWidths.push({ wch: Math.max(maxWidth, 10) }); // Minimum 10 characters
    }
    
    newWorksheet['!cols'] = colWidths;
    
    // Copy other metadata if they exist
    if (worksheet['!rows']) newWorksheet['!rows'] = worksheet['!rows'];
    if (worksheet['!merges']) newWorksheet['!merges'] = worksheet['!merges'];
    
    utils.book_append_sheet(workbook, newWorksheet, sheetName);
  } else {
    // Auto-size columns even when there's no summary data
    const dataRange = utils.decode_range(worksheet['!ref'] || 'A1');
    const maxCol = dataRange.e.c;
    const colWidths = [];
    
    for (let col = 0; col <= maxCol; col++) {
      let maxWidth = 10; // Minimum width
      
      // Check all cells in this column
      for (let row = 0; row <= dataRange.e.r; row++) {
        const cellAddress = utils.encode_cell({ r: row, c: col });
        const cell = worksheet[cellAddress];
        
        if (cell && cell.v) {
          const cellValue = String(cell.v);
          const estimatedWidth = cellValue.length * 1.2;
          if (estimatedWidth > maxWidth) {
            maxWidth = Math.min(estimatedWidth, 50);
          }
        }
      }
      
      colWidths.push({ wch: Math.max(maxWidth, 10) });
    }
    
    worksheet['!cols'] = colWidths;
    utils.book_append_sheet(workbook, worksheet, sheetName);
  }
  
  writeFile(workbook, filename, { compression: true });
}

export function parseCSVToObjects(csvData) {
  const { data } = Papa.parse(csvData, {
    header: true,
    skipEmptyLines: true
  });
  return data;
}

export function isXLSXFormat(key) {
  return key === 'xlsx'
}

export function isPDFFormat(key) {
  return key === 'pdf'
}

export function getDownloadFormat(preferenceKey) {
  const defaultDownloadFormats = {
    orders: 'csv',
    manual_inventory: 'csv',
    stock_availability: 'csv',
    inventory_valuation: 'csv',
    product_movements: 'csv',
    draft_order_list: 'csv',
    order_list: 'csv'
  };

  let userPreferences = {};

  const storedPreferences = localStorage.getItem('preferred_download_formats');
  try {
    userPreferences = storedPreferences ? JSON.parse(storedPreferences) : {};
  } catch {
    userPreferences = {};
  }

  const mergedFormats = { ...defaultDownloadFormats, ...userPreferences };

  if (preferenceKey) {
    return mergedFormats[preferenceKey] || 'csv';
  }

  return mergedFormats;
}

export function getSepaDownloadURL(sepa_id) {
  const crm_url = getCRMURL();
  return crm_url.u+'assets/'+sepa_id+'?download&access_token='+crm_url.t;
}

async function loadUnicodeFont(pdfDoc) {
  try {
    pdfDoc.registerFontkit(fontkit);
    const res = await fetch('/fonts/Roboto-Regular.ttf');
    if (!res.ok) throw new Error("Couldn't load TTF font");
    const buffer = await res.arrayBuffer();
    return await pdfDoc.embedFont(buffer);
  } catch (err) {
    console.warn('Falling back to StandardFonts due to font load error:', err);
    return null;
  }
}

export async function generateAndDownloadPDF(title, headers, keys, processedData, filename, options = {}) {
  const {
    margin = 50,
    fontSize = 12,
    lineHeight = 20,
    columnWidth = 140,
    autoFitColumns = true,
    orientation = 'landscape',
    summaryData = null
  } = options;

  const pdfDoc = await PDFDocument.create();
  const unicodeFont = await loadUnicodeFont(pdfDoc);
  const font = unicodeFont || await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  // Calculate table width and page widthu
  const effectiveColumnWidth = autoFitColumns ? 200 : columnWidth;
  const tableWidth = headers.length * effectiveColumnWidth;
  
  // Calculate page width with equal left and right margins
  // Minimum width ensures content fits within margins on both sides
  const minPageWidth = tableWidth + 2 * margin;
  const basePageWidth = orientation === 'landscape' ? 842 : 595;
  const pageWidth = Math.max(basePageWidth, minPageWidth);
  
  // Calculate horizontal centering offset to ensure equal margins
  const horizontalOffset = (pageWidth - tableWidth) / 2;
  
  const pageSize = [pageWidth, orientation === 'landscape' ? 595 : 842];
  let page = pdfDoc.addPage(pageSize);
  let yPosition = page.getHeight() - margin;

  const wrapText = (text, font, size, maxWidth) => {
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';
    words.forEach(word => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (font.widthOfTextAtSize(testLine, size) <= maxWidth) {
        currentLine = testLine;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = '';
        for (let char of word) {
          const test = `${currentLine}${char}`;
          if (font.widthOfTextAtSize(test, size) > maxWidth) {
            if (currentLine) lines.push(currentLine);
            currentLine = char;
          } else {
            currentLine = test;
          }
        }
      }
    });
    if (currentLine) lines.push(currentLine);
    return lines;
  };

  const getCellLines = (text, font, size, maxWidth) => {
    const parts = text.split('\n');
    let allLines = [];
    parts.forEach(part => {
      const wrapped = wrapText(part.trim(), font, size, maxWidth);
      allLines = [...allLines, ...wrapped];
    });
    return allLines;
  };

  // Draw summary section if provided (top-left)
  if (summaryData && typeof summaryData === 'object') {
    const summaryYStart = yPosition;
    const summaryX = margin;
    const summaryLineHeight = 18;
    let summaryY = summaryYStart;
    const isProductMovementsHeader = summaryData.headerFormat === 'productMovements';

    if (isProductMovementsHeader) {
      getProductMovementsHeaderRows(summaryData).forEach((field) => {
        if (field.isSection) {
          page.drawText(field.label, {
            x: summaryX,
            y: summaryY,
            size: fontSize,
            font: boldFont,
          });
        } else {
          const labelSuffix = field.label.endsWith(':') ? ' ' : ': ';
          const fullText = `${field.label}${labelSuffix}${field.value ?? ''}`;
          page.drawText(fullText, {
            x: summaryX,
            y: summaryY,
            size: fontSize,
            font: font,
          });
        }
        summaryY -= summaryLineHeight;
      });
    } else {
    const summaryLabels = [
      'Apothekenname',
      'Adresse',
      'Erstellungsdatum des Berichts',
      'Stichtag der Auswertung',
      // Add Lagerwert only for "Im Bericht verwendete Daten" report (when lagerwert exists)
      ...(summaryData.lagerwert ? ['Lagerwert'] : []),
      // Exclude "Auswertungszeitraum (von/bis)" from Lagerverfügbarkeitsbericht report
      ...(filename.includes('Lagerverfügbarkeit') ? [] : ['Auswertungszeitraum (von/bis)'])
    ];

    const summaryValues = [
      summaryData.apothekenname || '',
      summaryData.adresse || '',
      summaryData.erstellungsdatum || '',
      summaryData.stichtag || '',
      // Add Lagerwert value only when it exists
      ...(summaryData.lagerwert ? [summaryData.lagerwert] : []),
      // Exclude auswertungszeitraum value from Lagerverfügbarkeitsbericht report
      ...(filename.includes('Lagerverfügbarkeit') ? [] : [summaryData.auswertungszeitraum || ''])
    ];
    
    summaryLabels.forEach((label, index) => {
      if (summaryValues[index]) {
        const labelText = `${label}:`;
        const valueText = summaryValues[index];
        const fullText = `${labelText} ${valueText}`;
        
        page.drawText(fullText, {
          x: summaryX,
          y: summaryY,
          size: fontSize,
          font: font,
        });
        summaryY -= summaryLineHeight;
      }
    });
    }
    
    yPosition = summaryY - 20; // Add spacing after summary
  }

  // Draw title centered horizontally
  const titleWidth = boldFont.widthOfTextAtSize(title, 18);
  page.drawText(title, {
    x: (pageWidth - titleWidth) / 2,
    y: yPosition,
    size: 18,
    font: boldFont,
  });
  yPosition -= 40;

  const drawHeaders = () => {
    let maxHeaderLines = 0;
    const headerLinesArray = headers.map((header) => getCellLines(header, boldFont, fontSize, effectiveColumnWidth));
    headerLinesArray.forEach((lines) => {
      if (lines.length > maxHeaderLines) maxHeaderLines = lines.length;
    });
    const headerHeight = maxHeaderLines * lineHeight;

    headers.forEach((_, i) => {
      const x = horizontalOffset + i * effectiveColumnWidth;
      const lines = headerLinesArray[i];
      let cellY = yPosition;
      lines.forEach((line, j) => {
        page.drawText(line, {
          x,
          y: cellY - j * lineHeight,
          size: fontSize,
          font: boldFont
        });
      });
    });
    yPosition -= headerHeight;
  };

  drawHeaders();

  // Draw data rows
  processedData.forEach(row => {
    let maxLines = 0;
    const cellLinesArray = keys.map(key => getCellLines(String(row[key] || ''), font, fontSize, effectiveColumnWidth));
    cellLinesArray.forEach(lines => {
      if (lines.length > maxLines) maxLines = lines.length;
    });
    const rowHeight = maxLines * lineHeight;

    // Check for page overflow
    if (yPosition - rowHeight < margin) {
      page = pdfDoc.addPage(pageSize);
      yPosition = page.getHeight() - margin;
      drawHeaders();
    }

    // Draw each cell
    keys.forEach((key, i) => {
      const x = horizontalOffset + i * effectiveColumnWidth;
      const lines = cellLinesArray[i];
      let cellY = yPosition;
      lines.forEach((line, j) => {
        page.drawText(line, {
          x,
          y: cellY - j * lineHeight,
          size: fontSize,
          font,
        });
      });
    });
    yPosition -= rowHeight;
  });

  // Add two empty rows at the end
  for (let i = 0; i < 2; i++) {
    const maxLines = 1;
    const rowHeight = maxLines * lineHeight;
    if (yPosition - rowHeight < margin) {
      page = pdfDoc.addPage(pageSize);
      yPosition = page.getHeight() - margin;
      drawHeaders();
    }
    yPosition -= rowHeight;
  }

  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function hasCompleteAddress(address) {
  if (!address) return false;

  const components = address?.components;
  return address?.name &&
          address?.surname &&
          components?.route &&
          components?.locality &&
          components?.postal_code &&
          components?.street_number;
}

export function validateAddressSection(address, customer) {
  const messages = []

  if (!address || !address.components) {
    messages.push('Adresse ist nicht verfügbar.')
    return messages
  }

  const { firstname, lastname } = customer || {}
  const { route, locality, postal_code } = address.components

  const validations = [
    {
      condition: !firstname || !lastname || firstname.length < 1 || lastname.length < 1,
      message: 'Name ist zu kurz.'
    },
    {
      condition: !route || route.length < 1,
      message: 'Straßenname ist zu kurz.'
    },
    {
      condition: !locality || locality.length < 1,
      message: 'Stadtname ist zu kurz.'
    },
    {
      condition: !postal_code || postal_code.length !== 5,
      message: 'Die Postleitzahl muss im Format 99999 angegeben werden.'
    }
  ]

  validations.forEach(({ condition, message }) => {
    if (condition) messages.push(message)
  })

  return messages
}

export function formatWithComma(val, withParts = false) {
  const formatted = String(val).replace('.', ',');
  if (withParts) return formatted;

  const [integer, fraction] = formatted.split(',');
  return fraction ? `${integer},${fraction}` : integer;
}
